import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Mail, Lightbulb, Trash2, Reply, Star, Search } from "lucide-react";

const STATUSES = ["New", "Under review", "Planned", "Done", "Not possible"];
const STATUS_COLOR = { "New": "bg-blue-100 text-blue-700", "Under review": "bg-amber-100 text-amber-700", "Planned": "bg-purple-100 text-purple-700", "Done": "bg-green-100 text-green-700", "Not possible": "bg-red-100 text-red-700" };

export default function AdminMessages() {
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const [m, s] = await Promise.all([api.get("/admin/contacts"), api.get("/admin/suggestions")]);
    setMessages(m.data.contacts); setSuggestions(s.data.suggestions);
  }, []);
  useEffect(() => { load(); }, [load]);

  const delMsg = async (id) => { await api.delete(`/admin/contacts/${id}`); toast.success("Deleted"); load(); };
  const setStatus = async (id, status) => { await api.put(`/admin/suggestions/${id}`, { status }); toast.success("Status updated"); load(); };
  const reply = async (id, cur) => {
    const r = window.prompt("Reply to user:", cur || "");
    if (r === null) return;
    await api.put(`/admin/suggestions/${id}`, { admin_reply: r }); toast.success("Reply saved"); load();
  };
  const delSugg = async (id) => { await api.delete(`/admin/suggestions/${id}`); toast.success("Deleted"); load(); };

  const filtered = (arr, fields) => !q ? arr : (arr || []).filter((x) => fields.some((f) => (x[f] || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-5">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Messages &amp; Suggestions</h1>
      <div className="flex gap-2">
        <button onClick={() => setTab("messages")} className={`flex-1 rounded-xl p-3 font-semibold flex items-center justify-center gap-2 ${tab === "messages" ? "bg-thali-green text-white shadow-btn-green" : "bg-thali-paper border border-thali-turmeric/30"}`} data-testid="admin-tab-messages"><Mail className="w-4 h-4" /> Messages ({messages?.length ?? "…"})</button>
        <button onClick={() => setTab("suggestions")} className={`flex-1 rounded-xl p-3 font-semibold flex items-center justify-center gap-2 ${tab === "suggestions" ? "bg-thali-green text-white shadow-btn-green" : "bg-thali-paper border border-thali-turmeric/30"}`} data-testid="admin-tab-suggestions"><Lightbulb className="w-4 h-4" /> Suggestions ({suggestions?.length ?? "…"})</button>
      </div>
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-thali-ink/40" />
        <input className="input-thali pl-10" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-msg-search" />
      </div>

      {tab === "messages" && (!messages ? <Loader /> : (
        <div className="space-y-3">
          {filtered(messages, ["name", "mobile", "message", "subject"]).map((m) => (
            <div key={m.id} className="card-thali" data-testid={`admin-message-${m.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-heading font-bold">{m.name} <span className="text-xs bg-thali-turmeric/30 rounded px-2 py-0.5">{m.subject || "General"}</span></div>
                  <div className="text-sm text-thali-ink/60">{m.mobile} {m.email}</div>
                </div>
                <div className="flex gap-2">
                  {m.email && <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "Your message")}`} className="w-10 h-10 rounded-lg bg-thali-cream grid place-items-center" title="Reply by email"><Reply className="w-4 h-4" /></a>}
                  <button onClick={() => delMsg(m.id)} className="w-10 h-10 rounded-lg bg-red-50 text-red-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-thali-ink/80 mt-2">{m.message}</p>
            </div>
          ))}
          {messages.length === 0 && <div className="text-center py-8 text-thali-ink/50">No messages.</div>}
        </div>
      ))}

      {tab === "suggestions" && (!suggestions ? <Loader /> : (
        <div className="space-y-3">
          {filtered(suggestions, ["name", "mobile", "message", "category"]).map((s) => (
            <div key={s.id} className="card-thali" data-testid={`admin-suggestion-${s.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-heading font-bold flex items-center gap-2">{s.category}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[s.status] || "bg-gray-100"}`}>{s.status}</span>
                  </div>
                  <div className="text-sm text-thali-ink/60">{s.name || "Anonymous"} {s.mobile}
                    {s.rating ? <span className="ml-2 inline-flex items-center gap-0.5">{Array.from({ length: s.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-thali-turmeric text-thali-turmericdark" />)}</span> : null}
                  </div>
                </div>
                <button onClick={() => delSugg(s.id)} className="w-10 h-10 rounded-lg bg-red-50 text-red-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-thali-ink/80 mt-2">{s.message}</p>
              {s.admin_reply && <div className="mt-2 text-sm bg-thali-cream rounded-lg p-2"><b>Reply:</b> {s.admin_reply}</div>}
              <div className="flex flex-wrap gap-2 mt-3 items-center">
                <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} className="input-thali h-10 w-auto text-sm" data-testid={`sugg-status-${s.id}`}>
                  {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
                <button onClick={() => reply(s.id, s.admin_reply)} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`sugg-reply-${s.id}`}><Reply className="w-4 h-4" /> Reply</button>
              </div>
            </div>
          ))}
          {suggestions.length === 0 && <div className="text-center py-8 text-thali-ink/50">No suggestions.</div>}
        </div>
      ))}
    </div>
  );
}
